import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Delete, LogOut, Trash2, Pencil, KeyRound } from "lucide-react";
import { API } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const authApi = (token) =>
  axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

function PinLogin({ onLogin }) {
  const [pin, setPin] = useState("");
  const [shake, setShake] = useState(false);
  const [error, setError] = useState("");
  const [emailMode, setEmailMode] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const submitEmail = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      sessionStorage.setItem("ws_admin_token", data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.response?.status === 429 ? "Troppi tentativi. Riprova tra 15 minuti." : "Credenziali errate, riprova.");
    }
  };

  const submit = useCallback(async (value) => {
    try {
      const { data } = await axios.post(`${API}/admin/login`, { pin: value });
      sessionStorage.setItem("ws_admin_token", data.token);
      onLogin(data.token);
    } catch (e) {
      setShake(true);
      setTimeout(() => setShake(false), 450);
      setPin("");
      setError(e.response?.status === 429 ? "Troppi tentativi. Riprova tra 15 minuti." : "PIN errato, riprova.");
    }
  }, [onLogin]);

  const press = useCallback((d) => {
    setPin((p) => {
      if (p.length >= 6) return p;
      const next = p + d;
      if (next.length === 6) submit(next);
      return next;
    });
  }, [submit]);

  useEffect(() => {
    const handler = (e) => {
      if (/^[0-9]$/.test(e.key)) press(e.key);
      if (e.key === "Backspace") setPin((p) => p.slice(0, -1));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [press]);

  return (
    <div className="min-h-screen bg-[#121212] text-[#F9F6F0] flex flex-col items-center justify-center px-5" data-testid="admin-pin-login">
      <p className="font-display text-3xl font-bold mb-2">WILDER<span className="text-[#E4BD44]">SQUAD</span></p>
      <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-10">Area riservata · inserisci il PIN</p>
      {error && <p className="text-sm text-[#e07a5f] mb-6" data-testid="pin-error">{error}</p>}
      {emailMode ? (
        <form onSubmit={submitEmail} className="w-full max-w-xs space-y-4" data-testid="admin-email-login-form">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" data-testid="admin-email-input"
            className="w-full bg-[#181818] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm" />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" data-testid="admin-password-input"
            className="w-full bg-[#181818] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm" />
          <button type="submit" data-testid="admin-email-login-btn"
            className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-wider text-sm py-3 hover:bg-[#E4BD44] transition-colors">
            Accedi
          </button>
        </form>
      ) : (
        <>
      <div className={`flex gap-4 mb-10 ${shake ? "pin-shake" : ""}`} data-testid="pin-dots">
        {["d1", "d2", "d3", "d4", "d5", "d6"].map((k, i) => (
          <span key={k} className={`w-4 h-4 rounded-full border transition-colors ${i < pin.length ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#4A4A4A]"}`} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3 font-mono-ws">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <button key={n} onClick={() => press(String(n))} data-testid={`pin-key-${n}`}
            className="w-20 h-16 text-2xl border border-[#2A2A2A] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
            {n}
          </button>
        ))}
        <span />
        <button onClick={() => press("0")} data-testid="pin-key-0"
          className="w-20 h-16 text-2xl border border-[#2A2A2A] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">0</button>
        <button onClick={() => setPin((p) => p.slice(0, -1))} data-testid="pin-key-back"
          className="w-20 h-16 flex items-center justify-center border border-[#2A2A2A] hover:border-[#E4BD44] transition-colors">
          <Delete size={20} />
        </button>
      </div>
        </>
      )}
      <button onClick={() => { setEmailMode((m) => !m); setError(""); }} data-testid="toggle-login-mode-btn"
        className="mt-8 text-xs uppercase tracking-[0.2em] text-[#9a9484] hover:text-[#D4AF37] transition-colors">
        {emailMode ? "← Usa il PIN" : "Accedi con email e password"}
      </button>
    </div>
  );
}

function Table({ cols, rows, render, testId }) {
  return (
    <div className="overflow-x-auto border border-[#2A2A2A]" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="bg-[#141414] text-left">
          <tr>{cols.map((c) => <th key={c} className="px-4 py-3 font-semibold text-xs uppercase tracking-wider">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-[#9a9484]">Nessun dato ancora.</td></tr>
          )}
          {rows.map((r, i) => <tr key={r.id || i} className="border-t border-[#2A2A2A]">{render(r)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange, token }) {
  const [cur, setCur] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 8) return toast.error("La nuova password deve avere almeno 8 caratteri");
    if (pw1 !== pw2) return toast.error("Le nuove password non coincidono");
    setSaving(true);
    try {
      await authApi(token).post("/admin/change-password", { current_password: cur, new_password: pw1 });
      toast.success("Password aggiornata con successo");
      setCur(""); setPw1(""); setPw2("");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel cambio password");
    } finally {
      setSaving(false);
    }
  };

  const cls = "w-full bg-[#181818] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-[#F9F6F0]";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#2A2A2A] text-[#F9F6F0]" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Cambia password</DialogTitle>
          <DialogDescription className="text-[#9a9484]">Account: info@wildersquad.com</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <input type="password" required value={cur} onChange={(e) => setCur(e.target.value)}
            placeholder="Password attuale" className={cls} data-testid="current-password-input" />
          <input type="password" required minLength={8} value={pw1} onChange={(e) => setPw1(e.target.value)}
            placeholder="Nuova password (min 8 caratteri)" className={cls} data-testid="new-password-input" />
          <input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)}
            placeholder="Conferma nuova password" className={cls} data-testid="confirm-password-input" />
          <button type="submit" disabled={saving} data-testid="save-password-btn"
            className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-wider text-sm py-3 hover:bg-[#E4BD44] disabled:opacity-60 transition-colors">
            {saving ? "Salvataggio…" : "Aggiorna password"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const FLOW_LABELS = { Tutti: "Tutti", assaggi_arte: "Assaggi d'Arte", wild_chef_jr: "Wild Chef Jr" };

const EMPTY_ART = { title: "", size: "", year: "2022-2023", technique: "Olio su tela", price: "", edition: "", image_url: "", description: "", nft: false, featured: false };

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("ws_admin_token"));
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ waitlist: [], leads: [], subscribers: [], contacts: [], transactions: [], artworks: [], videos: [], orders: [], salento: [], igdms: { items: [], stats: { today: 0, week: 0, total: 0 }, flows: {} }, optin: { assaggi: [], corso_bambini: [] } });
  const [orderFilter, setOrderFilter] = useState("Tutti");
  const [igFilter, setIgFilter] = useState("Tutti");
  const [videoForm, setVideoForm] = useState({ url: "", title: "", handle: "" });
  const [editArt, setEditArt] = useState(null);
  const [isNewArt, setIsNewArt] = useState(false);

  const load = useCallback(async (tk) => {
    const c = authApi(tk);
    try {
      const [s, wl, ld, sub, ct, tx, art, vid, ord, sal, ig, opt] = await Promise.all([
        c.get("/admin/stats"), c.get("/admin/waitlist"), c.get("/admin/leads"),
        c.get("/admin/subscribers"), c.get("/admin/contacts"), c.get("/admin/transactions"),
        axios.get(`${API}/artworks`), axios.get(`${API}/videos`), c.get("/admin/orders"),
        c.get("/admin/salento-waitlist"), c.get("/admin/ig-dms"), c.get("/admin/optin-leads"),
      ]);
      setStats(s.data);
      setData({ waitlist: wl.data, leads: ld.data, subscribers: sub.data, contacts: ct.data, transactions: tx.data, artworks: art.data, videos: vid.data, orders: ord.data, salento: sal.data, igdms: ig.data, optin: opt.data });
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem("ws_admin_token");
        setToken(null);
      }
    }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);
  const [promoSending, setPromoSending] = useState(false);
  const [showPwDialog, setShowPwDialog] = useState(false);

  if (!token) return <PinLogin onLogin={setToken} />;

  const c = authApi(token);
  const logout = () => { sessionStorage.removeItem("ws_admin_token"); setToken(null); };

  const sendSalentoPromo = async () => {
    if (!window.confirm("Inviare l'email promo Salento a tutta la lista newsletter?")) return;
    setPromoSending(true);
    try {
      const { data } = await c.post("/admin/salento-promo");
      toast.success(data.queued ? `Promo in invio in background a ${data.queued} iscritti` : "Nessun nuovo iscritto: promo già inviata a tutti");
    } catch {
      toast.error("Errore nell'invio della promo");
    } finally {
      setPromoSending(false);
    }
  };

  const toggleIgFlow = async (flow, enabled) => {
    setData((d) => ({ ...d, igdms: { ...d.igdms, flows: { ...d.igdms.flows, [flow]: enabled } } }));
    try {
      await c.put(`/admin/ig-flows/${flow}`, { enabled });
      toast.success(`Flusso ${flow === "assaggi_arte" ? "Assaggi d'Arte" : "Wild Chef Jr"} ${enabled ? "attivato" : "disattivato"}`);
    } catch {
      toast.error("Errore nell'aggiornamento del flusso");
      load(token);
    }
  };

  const fulfillOrder = async (sessionId) => {
    try {
      await c.put(`/admin/orders/${sessionId}/fulfill`);
      toast.success("Ordine segnato come evaso");
      load(token);
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const addVideo = async (e) => {
    e.preventDefault();
    try {
      await c.post("/admin/videos", videoForm);
      setVideoForm({ url: "", title: "", handle: "" });
      toast.success("Video aggiunto al wall");
      load(token);
    } catch { toast.error("Errore nell'aggiunta"); }
  };

  const delVideo = async (id) => {
    await c.delete(`/admin/videos/${id}`);
    toast.success("Video rimosso");
    load(token);
  };

  const saveArt = async (e) => {
    e.preventDefault();
    const payload = { ...editArt, price: editArt.price ? Number(editArt.price) : null };
    delete payload.id; delete payload.collection; delete payload.created_at; delete payload.nft_platform;
    try {
      if (isNewArt) await c.post("/admin/artworks", payload);
      else await c.put(`/admin/artworks/${editArt.id}`, payload);
      toast.success("Opera salvata");
      setEditArt(null);
      load(token);
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const delArt = async (id) => {
    await c.delete(`/admin/artworks/${id}`);
    toast.success("Opera eliminata");
    load(token);
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "");

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      <header className="bg-[#121212] text-[#F9F6F0] px-5 lg:px-10 py-4 flex items-center justify-between">
        <p className="font-display text-xl font-bold">WILDER<span className="text-[#E4BD44]">SQUAD</span> <span className="text-[#D4AF37] text-sm font-normal">· Dashboard</span></p>
        <div className="flex items-center gap-5">
          <button onClick={() => setShowPwDialog(true)} className="flex items-center gap-2 text-xs uppercase tracking-wider hover:text-[#D4AF37] transition-colors" data-testid="admin-change-password-btn">
            <KeyRound size={14} /> Password
          </button>
          <button onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-wider hover:text-[#D4AF37] transition-colors" data-testid="admin-logout">
            <LogOut size={14} /> Esci
          </button>
        </div>
      </header>
      <ChangePasswordDialog open={showPwDialog} onOpenChange={setShowPwDialog} token={token} />

      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-8">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-[#2A2A2A] border border-[#2A2A2A] mb-8" data-testid="admin-stats">
            {[
              ["Iscritti newsletter", stats.subscribers],
              ["Lead servizi", stats.leads],
              ["Lista d'attesa", stats.waitlist_total],
              ["Paganti evento", `${stats.paid_people}/${stats.target}`],
              ["Incasso caparre", `€${stats.deposit_revenue.toLocaleString("it-IT")}`],
            ].map(([l, v]) => (
              <div key={l} className="bg-[#0A0A0A] p-5">
                <p className="font-display text-3xl font-black">{v}</p>
                <p className="text-[10px] uppercase tracking-[0.15em] text-[#9a9484] mt-1">{l}</p>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="waitlist">
          <TabsList className="bg-[#141414] rounded-none border border-[#2A2A2A] flex-wrap h-auto">
            {[["waitlist", "Lista d'attesa"], ["orders", "Ordini"], ["salento", "Lista Salento"], ["igdms", "DM Automation"], ["optin", "Leads Guide"], ["leads", "Lead"], ["newsletter", "Newsletter"], ["contacts", "Messaggi"], ["transactions", "Transazioni"], ["artworks", "Opere"], ["videos", "Video Wall"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} className="rounded-none data-[state=active]:bg-[#E4BD44] data-[state=active]:text-[#121212]" data-testid={`admin-tab-${v}`}>{l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="waitlist" className="mt-6">
            <Table testId="admin-waitlist-table" cols={["Nome", "Email", "Città", "Fascia", "Persone", "Stato", "Data"]}
              rows={data.waitlist}
              render={(r) => (<>
                <td className="px-4 py-3">{r.name}</td><td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3">{r.city}</td><td className="px-4 py-3">€{r.tier}</td>
                <td className="px-4 py-3">{r.num_people}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${r.status === "paid" ? "bg-[#E4BD44] text-black" : "bg-[#2A2A2A]"}`}>
                    {r.status === "paid" ? "Pagato" : "In attesa"}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-[#9a9484]">{fmtDate(r.created_at)}</td>
              </>)} />
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-orders-filters">
              {["Tutti", "Esperienze", "Digitali", "Arte Originale", "Merchandising"].map((f) => (
                <button key={f} onClick={() => setOrderFilter(f)} data-testid={`orders-filter-${f}`}
                  className={`text-xs uppercase tracking-wider px-3 py-1.5 border transition-colors ${orderFilter === f ? "bg-[#E4BD44] text-black border-[#E4BD44]" : "border-[#2A2A2A] hover:border-[#D4AF37]"}`}>
                  {f}
                </button>
              ))}
            </div>
            <Table testId="admin-orders-table" cols={["Prodotto", "Email", "Categoria", "Importo", "Evasione", "Data", ""]}
              rows={data.orders.filter((r) => orderFilter === "Tutti" || r.category === orderFilter)}
              render={(r) => (<>
                <td className="px-4 py-3 max-w-[220px] truncate">{r.title}</td>
                <td className="px-4 py-3 text-xs">{r.email || r.receipt_email || "—"}</td>
                <td className="px-4 py-3 text-xs text-[#E4BD44]">{r.category}</td>
                <td className="px-4 py-3">€{((r.amount || 0) / 100).toLocaleString("it-IT")}</td>
                <td className="px-4 py-3">
                  {r.fulfillment === "completed" ? (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#E4BD44] text-black">Evaso</span>
                  ) : r.fulfillment === "pending_fulfillment" ? (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#8A3324] text-white">POD da evadere</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#2A2A2A]">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-[#9a9484]">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3">
                  {r.is_merch && r.fulfillment !== "completed" && (
                    <button onClick={() => fulfillOrder(r.session_id)} data-testid={`fulfill-order-${r.session_id}`}
                      className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#E4BD44] text-[#E4BD44] hover:bg-[#E4BD44] hover:text-black transition-colors">
                      Segna evaso
                    </button>
                  )}
                </td>
              </>)} />
          </TabsContent>

          <TabsContent value="salento" className="mt-6">
            <div className="mb-4">
              <button onClick={sendSalentoPromo} disabled={promoSending} data-testid="admin-salento-promo-button"
                className="text-xs uppercase tracking-wider px-4 py-2 border border-[#E4BD44] text-[#E4BD44] hover:bg-[#E4BD44] hover:text-black transition-colors disabled:opacity-60">
                {promoSending ? "Invio in corso…" : "📣 Invia promo Salento alla newsletter"}
              </button>
            </div>
            <Table testId="admin-salento-table" cols={["Data", "Nome", "Email", "WhatsApp", "Serata", "Fonte"]}
              rows={data.salento}
              render={(r) => (<>
                <td className="px-4 py-3 text-xs text-[#9a9484]">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3">{r.nome}</td>
                <td className="px-4 py-3 text-xs">{r.email}</td>
                <td className="px-4 py-3 text-xs">{r.whatsapp}</td>
                <td className="px-4 py-3 text-xs text-[#E4BD44]">{r.serata_preferita}</td>
                <td className="px-4 py-3 text-xs">{r.fonte || "—"}</td>
              </>)} />
          </TabsContent>

          <TabsContent value="igdms" className="mt-6">
            <div className="grid grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A] mb-6" data-testid="ig-dm-stats">
              {[["Oggi", data.igdms.stats.today], ["Ultimi 7 giorni", data.igdms.stats.week], ["Totale", data.igdms.stats.total]].map(([l, v]) => (
                <div key={l} className="bg-[#0A0A0A] p-4 text-center">
                  <p className="font-display text-3xl font-black text-[#E4BD44]">{v}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#9a9484]">DM inviati · {l}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              {[["assaggi_arte", "Assaggi d'Arte"], ["wild_chef_jr", "Wild Chef Jr"]].map(([f, l]) => (
                <label key={f} className="flex items-center gap-2 text-xs cursor-pointer border border-[#2A2A2A] px-3 py-2" data-testid={`ig-flow-toggle-${f}`}>
                  <input type="checkbox" checked={data.igdms.flows[f] !== false}
                    onChange={(e) => toggleIgFlow(f, e.target.checked)} className="accent-[#E4BD44] w-4 h-4" />
                  Flusso {l}: <span className={data.igdms.flows[f] !== false ? "text-[#E4BD44]" : "text-[#8A3324]"}>{data.igdms.flows[f] !== false ? "ON" : "OFF"}</span>
                </label>
              ))}
              <div className="flex gap-2 ml-auto">
                {["Tutti", "assaggi_arte", "wild_chef_jr"].map((f) => (
                  <button key={f} onClick={() => setIgFilter(f)} data-testid={`ig-filter-${f}`}
                    className={`text-xs uppercase tracking-wider px-3 py-1.5 border transition-colors ${igFilter === f ? "bg-[#E4BD44] text-black border-[#E4BD44]" : "border-[#2A2A2A] hover:border-[#D4AF37]"}`}>
                    {FLOW_LABELS[f]}
                  </button>
                ))}
              </div>
            </div>
            <Table testId="admin-igdms-table" cols={["Data", "Utente", "Commento", "Keyword", "Flusso", "Stato"]}
              rows={data.igdms.items.filter((r) => igFilter === "Tutti" || r.flow_triggered === igFilter)}
              render={(r) => (<>
                <td className="px-4 py-3 text-xs text-[#9a9484]">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3 text-xs">@{r.username || r.user_id}</td>
                <td className="px-4 py-3 text-xs max-w-[220px] truncate" title={r.comment_text}>{r.comment_text}</td>
                <td className="px-4 py-3 text-xs text-[#E4BD44]">{r.keyword_matched}</td>
                <td className="px-4 py-3 text-xs">{r.flow_triggered === "assaggi_arte" ? "Assaggi d'Arte" : "Wild Chef Jr"}</td>
                <td className="px-4 py-3">
                  {r.dm_sent ? (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#E4BD44] text-black">Inviato</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#8A3324] text-white" title={r.dm_error || ""}>Non inviato</span>
                  )}
                </td>
              </>)} />
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <Table testId="admin-leads-table" cols={["Target", "Nome", "Email", "Telefono", "Note", "Data"]}
              rows={data.leads}
              render={(r) => (<>
                <td className="px-4 py-3 uppercase text-xs font-semibold text-[#E4BD44]">{r.target}</td>
                <td className="px-4 py-3">{r.name}</td><td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3">{r.phone || "—"}</td>
                <td className="px-4 py-3 text-xs max-w-xs truncate">{r.message || "—"}</td>
                <td className="px-4 py-3 text-xs text-[#9a9484]">{fmtDate(r.created_at)}</td>
              </>)} />
          </TabsContent>

          <TabsContent value="optin" className="mt-6">
            <div className="flex flex-wrap items-center gap-4 mb-5" data-testid="admin-optin-counts">
              {[["Assaggi d'Arte", data.optin.assaggi.length, "optin-count-assaggi"], ["Wild Chef Jr", data.optin.corso_bambini.length, "optin-count-jr"]].map(([l, n, tid]) => (
                <div key={l} className="border border-[#2A2A2A] bg-[#141414] px-5 py-3" data-testid={tid}>
                  <p className="font-display text-2xl font-black text-[#c4704b]">{n}</p>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#9a9484] mt-1">{l}</p>
                </div>
              ))}
              {data.optin.followups && [
                ["Follow-up 1", data.optin.followups.fu1, "optin-fu1"],
                ["Follow-up 2", data.optin.followups.fu2, "optin-fu2"],
                ["Follow-up 3", data.optin.followups.fu3, "optin-fu3"],
                ["Email totali", data.optin.followups.total_sent, "optin-fu-total"],
              ].map(([l, n, tid]) => (
                <div key={l} className="border border-[#2A2A2A] bg-[#141414] px-5 py-3" data-testid={tid}>
                  <p className="font-display text-2xl font-black">{n}</p>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#9a9484] mt-1">{l}</p>
                </div>
              ))}
              {data.optin.followups && (
                <div className="border border-[#E4BD44] bg-[#141414] px-5 py-3" data-testid="optin-converted">
                  <p className="font-display text-2xl font-black text-[#E4BD44]">
                    {data.optin.followups.converted}
                    <span className="text-sm font-normal text-[#9a9484] ml-2">
                      ({data.optin.assaggi.length ? Math.round((data.optin.followups.converted / data.optin.assaggi.length) * 100) : 0}%)
                    </span>
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#9a9484] mt-1">Lead convertiti</p>
                </div>
              )}
              <button data-testid="run-followups-btn"
                onClick={async () => {
                  try {
                    const { data: r } = await c.post("/admin/leads-followups/run");
                    window.alert(`Follow-up inviati ora: ${r.sent}`);
                  } catch { window.alert("Errore nell'invio follow-up"); }
                }}
                className="text-xs uppercase tracking-wider px-4 py-2 border border-[#c4704b] text-[#c4704b] hover:bg-[#c4704b] hover:text-white transition-colors">
                Invia follow-up ora
              </button>
            </div>
            <Table testId="admin-optin-table" cols={["Nome", "Email", "Fonte", "Follow-up", "Stato", "Data"]}
              rows={[...data.optin.assaggi.map((r) => ({ ...r, fonte: "Assaggi d'Arte" })), ...data.optin.corso_bambini.map((r) => ({ ...r, fonte: "Wild Chef Jr" }))].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))}
              render={(r) => (<>
                <td className="px-4 py-3">{r.nome}</td><td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${r.fonte === "Wild Chef Jr" ? "bg-[#c4704b] text-white" : "bg-[#2A2A2A]"}`}>{r.fonte}</span>
                </td>
                <td className="px-4 py-3 text-xs">
                  {r.fonte === "Assaggi d'Arte"
                    ? ["fu1", "fu2", "fu3"].map((k) => (
                        <span key={k} title={k} className={`inline-block w-2.5 h-2.5 rounded-full mr-1.5 ${r[k] && r[k] !== "skipped_prenotato" ? "bg-[#c4704b]" : "bg-[#2A2A2A]"}`} />
                      ))
                    : <span className="text-[#9a9484]">—</span>}
                </td>
                <td className="px-4 py-3">
                  {r.convertito
                    ? <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#E4BD44] text-black">Prenotato</span>
                    : <span className="text-[10px] uppercase tracking-wider text-[#9a9484]">Lead</span>}
                </td>
                <td className="px-4 py-3 text-xs text-[#9a9484]">{fmtDate(r.created_at)}</td>
              </>)} />
          </TabsContent>

          <TabsContent value="newsletter" className="mt-6">
            <Table testId="admin-subscribers-table" cols={["Email", "Lead magnet", "Data"]}
              rows={data.subscribers}
              render={(r) => (<>
                <td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3 text-xs">{r.lead_magnet}</td>
                <td className="px-4 py-3 text-xs text-[#9a9484]">{fmtDate(r.created_at)}</td>
              </>)} />
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            <Table testId="admin-contacts-table" cols={["Nome", "Email", "Oggetto", "Messaggio", "Data"]}
              rows={data.contacts}
              render={(r) => (<>
                <td className="px-4 py-3">{r.name}</td><td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3">{r.subject || "—"}</td>
                <td className="px-4 py-3 text-xs max-w-xs truncate">{r.message}</td>
                <td className="px-4 py-3 text-xs text-[#9a9484]">{fmtDate(r.created_at)}</td>
              </>)} />
          </TabsContent>

          <TabsContent value="transactions" className="mt-6">
            <Table testId="admin-transactions-table" cols={["Email", "Importo", "Stato", "Session", "Data"]}
              rows={data.transactions}
              render={(r) => (<>
                <td className="px-4 py-3">{r.email || "—"}</td>
                <td className="px-4 py-3">€{((r.amount || 0) / 100).toLocaleString("it-IT")}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${r.payment_status === "paid" ? "bg-[#E4BD44] text-black" : "bg-[#2A2A2A]"}`}>
                    {r.payment_status}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs max-w-[160px] truncate">{r.session_id}</td>
                <td className="px-4 py-3 text-xs text-[#9a9484]">{fmtDate(r.created_at)}</td>
              </>)} />
          </TabsContent>

          <TabsContent value="artworks" className="mt-6">
            <button className="btn-primary mb-5 !py-2.5 !px-5 text-xs" data-testid="admin-add-artwork"
              onClick={() => { setEditArt({ ...EMPTY_ART }); setIsNewArt(true); }}>
              + Nuova opera
            </button>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.artworks.map((a) => (
                <div key={a.id} className="border border-[#2A2A2A] bg-[#141414]" data-testid={`admin-artwork-${a.id}`}>
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img src={a.image_url} alt={a.title} className="w-full h-full object-cover" />
                    {a.available === false && (
                      <span className="absolute top-2 left-2 bg-[#8A3324] text-white text-[10px] uppercase tracking-wider px-2 py-0.5" data-testid={`admin-art-sold-${a.id}`}>
                        Venduta / non disponibile
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="font-display font-bold">{a.title}</p>
                    <p className="text-xs text-[#9a9484] mb-3">{a.size} · {a.price ? `€${a.price.toLocaleString("it-IT")}` : "su richiesta"}</p>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditArt({ ...a }); setIsNewArt(false); }} className="btn-outline !py-1.5 !px-3 text-[10px]" data-testid={`admin-edit-art-${a.id}`}>
                        <Pencil size={12} /> Modifica
                      </button>
                      <button onClick={() => delArt(a.id)} className="btn-outline !py-1.5 !px-3 text-[10px] !border-[#E4BD44] !text-[#E4BD44]" data-testid={`admin-del-art-${a.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="videos" className="mt-6">
            <form onSubmit={addVideo} className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-video-form">
              <input required className="ws-input sm:col-span-2" placeholder="Incolla URL (Reel / TikTok / Short)" value={videoForm.url}
                onChange={(e) => setVideoForm({ ...videoForm, url: e.target.value })} data-testid="admin-video-url" />
              <input required className="ws-input" placeholder="Titolo" value={videoForm.title}
                onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} data-testid="admin-video-title" />
              <div className="flex gap-2">
                <input className="ws-input" placeholder="@handle" value={videoForm.handle}
                  onChange={(e) => setVideoForm({ ...videoForm, handle: e.target.value })} data-testid="admin-video-handle" />
                <button type="submit" className="btn-primary !px-4" data-testid="admin-video-add">+</button>
              </div>
            </form>
            <Table testId="admin-videos-table" cols={["Piattaforma", "Titolo", "Handle", "URL", ""]}
              rows={data.videos}
              render={(r) => (<>
                <td className="px-4 py-3 uppercase text-xs font-semibold text-[#E4BD44]">{r.platform}</td>
                <td className="px-4 py-3">{r.title}</td>
                <td className="px-4 py-3 text-xs">{r.handle}</td>
                <td className="px-4 py-3 text-xs max-w-[200px] truncate"><a href={r.url} target="_blank" rel="noopener noreferrer" className="ws-link">{r.url}</a></td>
                <td className="px-4 py-3">
                  <button onClick={() => delVideo(r.id)} data-testid={`admin-del-video-${r.id}`} className="text-[#E4BD44] hover:text-[#C9A227]"><Trash2 size={15} /></button>
                </td>
              </>)} />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!editArt} onOpenChange={(o) => !o && setEditArt(null)}>
        <DialogContent className="bg-[#0A0A0A] border-[#2A2A2A] rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{isNewArt ? "Nuova opera" : "Modifica opera"}</DialogTitle>
            <DialogDescription className="text-xs text-[#9a9484]">Compila i dettagli dell'opera e salva per aggiornare la galleria.</DialogDescription>
          </DialogHeader>
          {editArt && (
            <form onSubmit={saveArt} className="grid grid-cols-2 gap-3" data-testid="admin-artwork-form">
              <input required className="ws-input col-span-2" placeholder="Titolo" value={editArt.title || ""}
                onChange={(e) => setEditArt({ ...editArt, title: e.target.value })} data-testid="art-form-title" />
              <input className="ws-input" placeholder="Dimensioni (es. 30x40 cm)" value={editArt.size || ""}
                onChange={(e) => setEditArt({ ...editArt, size: e.target.value })} data-testid="art-form-size" />
              <input className="ws-input" placeholder="Prezzo € (vuoto = su richiesta)" type="number" value={editArt.price ?? ""}
                onChange={(e) => setEditArt({ ...editArt, price: e.target.value })} data-testid="art-form-price" />
              <input className="ws-input" placeholder="Anno" value={editArt.year || ""}
                onChange={(e) => setEditArt({ ...editArt, year: e.target.value })} />
              <input className="ws-input" placeholder="Edizione (es. 2/10)" value={editArt.edition || ""}
                onChange={(e) => setEditArt({ ...editArt, edition: e.target.value })} />
              <input className="ws-input" placeholder="Riproduzioni disponibili (su 10)" type="number" value={editArt.editions_left ?? ""}
                onChange={(e) => setEditArt({ ...editArt, editions_left: e.target.value === "" ? null : Number(e.target.value) })} data-testid="art-form-editions" />
              <input className="ws-input col-span-2" placeholder="URL immagine (incolla qui la foto reale)" value={editArt.image_url || ""}
                onChange={(e) => setEditArt({ ...editArt, image_url: e.target.value })} data-testid="art-form-image" />
              <label className="flex items-center gap-2 text-sm text-[#C9C4B8] col-span-2 cursor-pointer" data-testid="art-form-available">
                <input type="checkbox" checked={editArt.available !== false}
                  onChange={(e) => setEditArt({ ...editArt, available: e.target.checked })}
                  className="accent-[#E4BD44] w-4 h-4" />
                Disponibile alla vendita (deseleziona se venduta)
              </label>
              <textarea rows={4} className="ws-input col-span-2" placeholder="Descrizione" value={editArt.description || ""}
                onChange={(e) => setEditArt({ ...editArt, description: e.target.value })} data-testid="art-form-description" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editArt.nft} onChange={(e) => setEditArt({ ...editArt, nft: e.target.checked })} /> NFT Crypto.com
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editArt.featured} onChange={(e) => setEditArt({ ...editArt, featured: e.target.checked })} /> In evidenza (Home)
              </label>
              <button type="submit" className="btn-primary col-span-2 justify-center" data-testid="art-form-save">Salva</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
